import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { messageShow } from "../../common/api/actions";
import { TableRow, IconButton, Tooltip, TableCell, Checkbox, Box } from "@mui/material";
import { Delete, Send, FileCopy, AccessTime as AccessTimeIcon, Stop, Archive, Cancel } from "@mui/icons-material";
import { NameTableCell, MessageTableCell, DateTableCell, StatusTableCell, ActionTableCell } from "./tableCells";
import { useHistory } from "react-router-dom";
import { createEvent } from "../api/action/mutation";
import routes from "../api/routes";
import { CircularProgress } from "@mui/material";

const EventCard = ({ event, handleDelete, inProgressEventId, setInProgressEventId, sendFunction, refreshFunction, handelCancel, eventType, fetchEvents, updateEvent, selectedEvents, setSelectedEvents, permissions, handleArchive }) => {
    const [status, setStatus] = useState(null);
    const dispatch = useDispatch();
    const history = useHistory();

    useEffect(() => {
        setStatus(event.status);
    }, [event]);

    const handleSelect = (e) => {
        e.stopPropagation();
        const isChecked = e.target.checked;
        if (isChecked) {
            setSelectedEvents((prev) => [...prev, event._id]);
        } else {
            setSelectedEvents((prev) => prev.filter(id => id !== event._id));
        }
    };

    const handleRowClick = () => {
        if (status === "Yet to Send") {
            history.push({
                pathname: routes.editEvent.path(eventType, event._id),
                state: { eventData: event }
            });
        } else {
            history.push({
                pathname: routes.viewEvent.path(eventType, event._id),
                state: { eventData: event }
            });
        }
    };

    const handleActionClick = (e, action) => {
        e.stopPropagation();
        if (action) action();
    };

    const handleSend = async () => {
        if (inProgressEventId) {
            dispatch(messageShow(`Another ${eventType} event is already in progress`));
            return;
        }
        setStatus("In Progress");
        event.status = "In Progress";
        try {
            setInProgressEventId(event._id);
            const response = await sendFunction(event._id);
            if (response.message === "Batch Attached" && eventType === "email") {
                dispatch(messageShow(response.message));
            } else if (response.message === "Completed") {
                setStatus("Completed");
                dispatch(messageShow(response.message));
            } else {
                if (response?.status === 200) {
                    setStatus("Yet to Send");
                } else {
                    setStatus("Failed");
                }
                dispatch(messageShow(response?.error || response?.message));
            }
        } catch (error) {
            console.error("Error sending event:", error);
            dispatch(messageShow("Error sending event. Please try again."));
        } finally {
            setInProgressEventId(null);
        }
    };

    const handleDeleteEvent = async () => {
        if (window.confirm(`Do you want to delete: ${event.name}?`)) {
            await handleDelete(event._id);
            setSelectedEvents((prev) => prev.filter(id => id !== event._id));
        }
    };

    const handleClone = async () => {
        try {
            const { _id, createdAt, updatedAt, ...baseEvent } = event;
            const clonedEventData = {
                ...baseEvent,
                name: `${event.name} (Clone)`,
                status: "Yet to Send",
                date: new Date().toISOString(),
                notificationType: eventType,
                scheduledTime: null,
                sentList: [],
            };
            const cloneResponse = await createEvent(clonedEventData);
            if (cloneResponse && !cloneResponse.error) {
                if (cloneResponse.event || cloneResponse.notification || cloneResponse.post) {
                    if (cloneResponse.event) updateEvent(cloneResponse.event);
                    if (cloneResponse.notification) updateEvent(cloneResponse.notification);
                    if (cloneResponse.post) updateEvent(cloneResponse.post);
                } else {
                    updateEvent(cloneResponse.data);
                }
                dispatch(messageShow("Event cloned successfully"));
            } else {
                throw new Error(cloneResponse.error || "Error cloning event");
            }
        } catch (error) {
            console.error("Error cloning event:", error);
            dispatch(messageShow("Error cloning event. Please try again."));
        }
    };

    const handleStop = async () => {
        try {
            dispatch(messageShow("Event stopped successfully"));
        } catch (error) {
            console.error("Error stopping event:", error);
            dispatch(messageShow("Error stopping event. Please try again."));
        }
    };

    const renderStatus = () => {
        const getStyle = (color) => ({
            padding: '4px 8px',
            borderRadius: '5px',
            fontSize: '12px',
            border: `1px solid ${color}`,
            backgroundColor: `${color}20`,
            color: color, width: '80px',
            height: '24px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'inline-flex',
            justifyContent: 'center',
            alignItems: 'center'
        });

        if (event.scheduledTime && event.status === "In Progress") {
            const scheduledDate = new Date(event.scheduledTime);
            const formatted = scheduledDate.toLocaleString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
            return (
                <Tooltip title={`Scheduled for: ${formatted}`}>
                    <span style={getStyle("#ff9800")}>
                        Scheduled
                    </span>
                </Tooltip>
            );
        }

        switch (status) {
            case "In Progress":
                return <span style={getStyle("#42a5f5")}>In Progress</span>;
            case "Cancelled":
                return <span style={getStyle("#ff2626ff")}>Stopped</span>;
            case "Yet to Send":
                return <span style={getStyle("#78909c")}>Not Sent</span>;
            case "Completed":
                return <span style={getStyle("#66bb6a")}>Sent</span>;
            case "Failed":
                return <span style={getStyle("#ef5350")}>Failed</span>;
            default:
                return <span style={getStyle("#ef5350")}>Failed</span>;
        }
    };

    const setActionButtons = () => {
        if (eventType === "email") {
            const isYetToSend = status === "Yet to Send";
            const isInProgress = status === "In Progress";
            const isCancelled = status === "Cancelled";
            const isCompleted = status === "Completed";
            const isScheduled = event.scheduledTime && event.status !== "Completed";

            const buttons = {
                send: isInProgress ? (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 40,
                            height: 40,
                        }}
                    >
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <Tooltip title={"Send"}>
                        <span>
                            <IconButton
                                aria-label="send"
                                size="medium"
                                onClick={(e) => handleActionClick(e, handleSend)}
                                disabled={!isYetToSend || inProgressEventId || !(permissions.canCreate && permissions.canEdit)}
                            >
                                <Send color={!isYetToSend || inProgressEventId || !(permissions.canCreate && permissions.canEdit) ? "disabled" : "primary"} />
                            </IconButton>
                        </span>
                    </Tooltip>
                ),
                clone: (
                    <Tooltip title="Clone">
                        <span>
                            <IconButton
                                aria-label="clone"
                                size="medium"
                                onClick={(e) => handleActionClick(e, handleClone)}
                                disabled={!permissions.canCreate}
                            >
                                <FileCopy color={permissions.canCreate ? "primary" : "disabled"} />
                            </IconButton>
                        </span>
                    </Tooltip>
                ),
                cancel: (
                    <Tooltip title="Stop">
                        <span>
                            <IconButton
                                aria-label="stop"
                                size="medium"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (window.confirm(`Are you sure you want to stop the event "${event.name}"?`)) {
                                        handelCancel(event._id)
                                    }
                                }}
                                disabled={!isInProgress || !permissions.canEdit}
                            >
                                <Cancel color={isInProgress && permissions.canEdit ? "error" : "disabled"} />
                            </IconButton>
                        </span>
                    </Tooltip>
                ),
                archive: (
                    <Tooltip title="Archive">
                        <span>
                            <IconButton
                                aria-label="archive"
                                size="medium"
                                onClick={(e) => {
                                    if (window.confirm(`Move the event to archive?`)) {
                                        handleArchive(event._id)
                                    }
                                }}
                                disabled={!(isCompleted || isCancelled) || !permissions.canEdit}
                            >
                                <Archive color={(isCompleted || isCancelled) && permissions.canEdit ? "secondary" : "disabled"} />
                            </IconButton>
                        </span>
                    </Tooltip>
                ),
                delete: (
                    <Tooltip title="Delete">
                        <span>
                            <IconButton
                                aria-label="delete"
                                size="medium"
                                onClick={(e) => handleActionClick(e, handleDeleteEvent)}
                                disabled={!permissions.canDelete}
                            >
                                <Delete color={permissions.canDelete ? "error" : "disabled"} />
                            </IconButton>
                        </span>
                    </Tooltip>
                ),
            };

            return (
                <div style={{ display: "flex", gap: "5px", justifyContent: "left", alignItems: "center" }}>
                    {(isYetToSend || isInProgress) ? buttons.send : null}
                    {isInProgress ? buttons.cancel : null}
                    {(isCompleted || isCancelled) ? buttons.archive : null}
                    {buttons.clone}
                </div>
            );
        } else {
            // For non-email event types
            const isYetToSend = status === "Yet to Send";
            const isInProgress = status === "In Progress";

            const buttons = {
                send: isInProgress ? (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 40,
                            height: 40,
                        }}
                    >
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <Tooltip title={"Send"}>
                        <span>
                            <IconButton
                                aria-label="send"
                                size="medium"
                                onClick={(e) => handleActionClick(e, handleSend)}
                                disabled={!isYetToSend || inProgressEventId || !(permissions.canCreate && permissions.canEdit)}
                            >
                                <Send color={!isYetToSend || inProgressEventId || !(permissions.canCreate && permissions.canEdit) ? "disabled" : "primary"} />
                            </IconButton>
                        </span>
                    </Tooltip>
                ),
                clone: (
                    <Tooltip title="Clone">
                        <span>
                            <IconButton
                                aria-label="clone"
                                size="medium"
                                onClick={(e) => handleActionClick(e, handleClone)}
                                disabled={!permissions.canCreate}
                            >
                                <FileCopy color={permissions.canCreate ? "primary" : "disabled"} />
                            </IconButton>
                        </span>
                    </Tooltip>
                ),
                delete: (
                    <Tooltip title="Delete">
                        <span>
                            <IconButton
                                aria-label="delete"
                                size="medium"
                                onClick={(e) => handleActionClick(e, handleDeleteEvent)}
                                disabled={!permissions.canDelete}
                            >
                                <Delete color={permissions.canDelete ? "error" : "disabled"} />
                            </IconButton>
                        </span>
                    </Tooltip>
                ),
            };

            return (
                <div style={{ display: "flex", gap: "5px", justifyContent: "center", alignItems: "center" }}>
                    {buttons.send}
                    {buttons.clone}
                </div>
            );
        }
    };


    const stripHtml = (html) => {
        const div = document.createElement("div");
        div.innerHTML = html;
        return div.textContent || div.innerText || "";
    };

    return (
        <TableRow key={event._id} onClick={handleRowClick} style={{ cursor: 'pointer' }} hover>
            <TableCell padding="checkbox">
                <Checkbox
                    onClick={(e) => e.stopPropagation()}
                    checked={selectedEvents.includes(event._id)}
                    onChange={handleSelect}
                />
            </TableCell>
            <NameTableCell>{event.name}</NameTableCell>
            <MessageTableCell title={stripHtml(event.message)}>
                {stripHtml(event.message)}
            </MessageTableCell>
            <DateTableCell>
                {event.scheduledTime ? (
                    <>
                        <AccessTimeIcon
                            style={{ marginRight: 3, marginBottom: 3, verticalAlign: 'middle', fontSize: '1rem', color: '#666' }}
                        />
                        {new Date(event.scheduledTime).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </>
                ) : (
                    new Date(event.date).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
                )}
            </DateTableCell>
            <StatusTableCell>{renderStatus()}</StatusTableCell>
            <ActionTableCell onClick={(e) => e.stopPropagation()}>
                {setActionButtons()}
            </ActionTableCell>
        </TableRow>
    );
};

export default EventCard;
